import { jsxs, jsx } from "react/jsx-runtime";
import { useContext, cloneElement } from "react";
import assert from "assert";
import { Box } from "../Box/Box.mjs";
import { Text } from "../Text/Text.mjs";
import { Columns } from "../Columns/Columns.mjs";
import { Column } from "../Column/Column.mjs";
import { Inline } from "../Inline/Inline.mjs";
import "../Text/TextContext.mjs";
import "../Heading/HeadingContext.mjs";
import "../../css/lineHeightContainer.css.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import "../../css/reset/reset.css.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../../css/typography.css.mjs";
import { IconExpandLess } from "../icons/IconExpandLess/IconExpandLess.mjs";
import { IconExpandMore } from "../icons/IconExpandMore/IconExpandMore.mjs";
import { useDisclosure } from "../Disclosure/useDisclosure.mjs";
import { virtualTouchable } from "../private/touchable/virtualTouchable.mjs";
import { hideFocusRingsClassName } from "../private/hideFocusRings/hideFocusRings.css.mjs";
import { Overlay } from "../private/Overlay/Overlay.mjs";
import { AccordionContext, validTones } from "./AccordionContext.mjs";
import { button, iconContainer, focusRing } from "./AccordionItem.css.mjs";
const itemSpaceForSize = {
  xsmall: "small",
  small: "medium",
  medium: "medium",
  standard: "medium",
  large: "large"
};
const AccordionItem = ({
  id,
  label,
  children,
  badge,
  size: sizeProp,
  tone: toneProp,
  icon,
  data,
  ...restProps
}) => {
  const accordionContext = useContext(AccordionContext);
  assert(
    !(accordionContext && sizeProp),
    "Size cannot be set on AccordionItem when inside Accordion. Size should be set on Accordion instead."
  );
  assert(
    !(accordionContext && toneProp),
    "Tone cannot be set on AccordionItem when inside Accordion. Tone should be set on Accordion instead."
  );
  assert(
    toneProp === void 0 || validTones.includes(toneProp),
    `The 'tone' prop should be one of the following: ${validTones.map((x) => `"${x}"`).join(", ")}`
  );
  assert(
    // @ts-expect-error
    !badge || badge.type.__isBadge__,
    `AccordionItem badge prop can only be an instance of Badge. e.g. <AccordionItem badge={<Badge>New</Badge>}>`
  );
  assert(
    !badge || badge.props.bleedY === void 0,
    "Badge elements cannot set the 'bleedY' prop when passed to an AccordionItem component"
  );
  assert(
    !icon || icon.props.size === void 0 && icon.props.tone === void 0,
    "Icons cannot set the 'size' or 'tone' prop when passed to an AccordionItem component"
  );
  const size = (accordionContext == null ? void 0 : accordionContext.size) ?? sizeProp ?? "large";
  const tone = (accordionContext == null ? void 0 : accordionContext.tone) ?? toneProp ?? "neutral";
  const weight = "medium";
  const itemSpace = itemSpaceForSize[size] ?? "none";
  assert(typeof label === "undefined" || typeof label === "string", "Label must be a string");
  const { expanded, buttonProps, contentProps } = useDisclosure({
    id,
    ...restProps.expanded !== void 0 ? {
      onToggle: restProps.onToggle,
      expanded: restProps.expanded
    } : {
      onToggle: restProps.onToggle
    }
  });
  return /* @__PURE__ */ jsxs(Box, { ...buildDataAttributes({ data, validateRestProps: restProps }), children: [
    /* @__PURE__ */ jsxs(Box, { position: "relative", display: "flex", children: [
      /* @__PURE__ */ jsx(
        Box,
        {
          component: "button",
          type: "button",
          cursor: "pointer",
          className: [button, virtualTouchable()],
          outline: "none",
          width: "full",
          textAlign: "left",
          ...buttonProps,
          children: /* @__PURE__ */ jsx(Box, { component: "span", position: "relative", children: /* @__PURE__ */ jsxs(Columns, { component: "span", space: itemSpace, children: [
            /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Box, { component: "span", display: "flex", children: [
              icon ? /* @__PURE__ */ jsx(
                Box,
                {
                  component: "span",
                  paddingRight: "small",
                  className: iconContainer[size],
                  children: cloneElement(icon, { size, tone })
                }
              ) : null,
              /* @__PURE__ */ jsxs(Inline, { component: "span", space: "small", alignY: "center", children: [
                /* @__PURE__ */ jsx(Text, { size, weight, tone, children: label }),
                badge ? cloneElement(badge, { bleedY: true }) : null
              ] })
            ] }) }),
            /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Text, { size, weight, tone: tone === "neutral" ? "secondary" : tone, children: expanded ? /* @__PURE__ */ jsx(IconExpandLess, {}) : /* @__PURE__ */ jsx(IconExpandMore, {}) }) })
          ] }) })
        }
      ),
      /* @__PURE__ */ jsx(
        Overlay,
        {
          boxShadow: "outlineFocus",
          borderRadius: "standard",
          transition: "fast",
          className: [focusRing, hideFocusRingsClassName]
        }
      )
    ] }),
    /* @__PURE__ */ jsx(Box, { paddingTop: itemSpace, display: expanded ? "block" : "none", ...contentProps, children })
  ] });
};
export {
  AccordionItem
};
